import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { InitiateVendingCardPayload, InitiateVendingPayload } from "../../../../../model/charge";
import { PaymentOptions } from "../../buy-electricity.types";
import {
	useInitiatePostpaidPaymentMutation,
	useInitiatePostpaidPayWithWalletMutation,
	useInitiatePostpaidNotificationPayWithCardMutation,
	useInitiatePostpaidNotificationPayWithWalletMutation,
	useInitiatePrepaidPaymentMutation,
	useInitiatePrepaidPayWithWalletMutation,
	useBorrowTokenMutation,
} from "../../../../../service/vending-service";
import PaymentProcessor from "../payment-processor";
import { getErrorMessage } from "../../../../../utils/error-handler";
import TransactionPinModal from "../../../components/transaction-pin-modal/TransactionPinModal";
import { RootState } from "../../../../../stores/store";
import { FeatureFlagName } from "../../../../../tenant/features/featureFlags";
import { useFeatureFlag } from "../../../../../tenant/features/useFeatureFlag";
import { describeError } from "../../../../../services/business-analytics/customFields";
import {
	BillingFailureReason,
	trackTransactionAuthorizedFailed,
	trackTransactionAuthorizedSuccess,
} from "../../../../../services/business-analytics/vendEvents";

export interface BuyElectricityTransactionPinProps {
	confirmationData: {
		utilityId: string;
		meterNumber: string;
		amount: string;
		paymentType: "prepaid" | "postpaid";
		showPreferRemoteTokenLoadOption?: boolean;
	};
	selectedPaymentOption: PaymentOptions;
	handleBack: () => void;
	onPaymentSuccess?: (reference: string) => void;
	onPaymentFailed: (reference: string, reason?: BillingFailureReason) => void;
	onForgetPin: () => void;
	onBorrowSuccess?: (reference: string) => void;
	billingDashNotificationId?: string;
}

export const BuyElectricityTransactionPin: React.FC<BuyElectricityTransactionPinProps> = ({
	confirmationData,
	selectedPaymentOption,
	handleBack,
	onPaymentSuccess,
	onPaymentFailed,
	onForgetPin,
	onBorrowSuccess,
	billingDashNotificationId,
}) => {
	const { t } = useTranslation();
	const initialRemoteTokenLoad = useSelector((state: RootState) => state.customerEnergyProfile.preferRemoteTokenLoad ?? false);
	const isRemoteTokenLoadEnabled = useFeatureFlag(FeatureFlagName.REMOTE_TOKEN_LOAD);

	const [showPaymentProcessor, setShowPaymentProcessor] = useState(false);
	const [paymentData, setPaymentData] = useState<{
		paymentUrl?: string;
		reference: string;
		vendingDetailsId?: string;
		skipRedirect: boolean;
	}>();

	const [initiatePrepaidPayment, { isLoading: isLoadingPrepaidCard }] = useInitiatePrepaidPaymentMutation();
	const [initiatePrepaidPayWithWallet, { isLoading: isLoadingPrepaidWallet }] = useInitiatePrepaidPayWithWalletMutation();
	const [initiatePostpaidPayment, { isLoading: isLoadingPostpaidCard }] = useInitiatePostpaidPaymentMutation();
	const [initiatePostpaidPayWithWallet, { isLoading: isLoadingPostpaidWallet }] = useInitiatePostpaidPayWithWalletMutation();
	const [initiateNotificationPayWithCard, { isLoading: isLoadingNotificationCard }] = useInitiatePostpaidNotificationPayWithCardMutation();
	const [initiateNotificationPayWithWallet, { isLoading: isLoadingNotificationWallet }] = useInitiatePostpaidNotificationPayWithWalletMutation();
	const [borrowToken, { isLoading: isLoadingBorrowToken }] = useBorrowTokenMutation();

	const isLoading =
		isLoadingPrepaidCard ||
		isLoadingPrepaidWallet ||
		isLoadingPostpaidCard ||
		isLoadingPostpaidWallet ||
		isLoadingNotificationCard ||
		isLoadingNotificationWallet ||
		isLoadingBorrowToken;

	const handlePinSubmit = async (pin: string, remoteTokenLoadForPayment?: boolean | null) => {
		try {
			const isPrepaid = confirmationData.paymentType === "prepaid";
			const isWallet = selectedPaymentOption === PaymentOptions.Wallet;
			const isBorrowToken = selectedPaymentOption === PaymentOptions.BorrowToken;
			const isBank = selectedPaymentOption === PaymentOptions.Bank;
			const isTransfer = selectedPaymentOption === PaymentOptions.Transfer;
			const paymentTypes: string[] = isBank ? ["BankTransfer"] : isTransfer ? ["OneTimeWallet"] : ["Card"];

			let response: any;

			if (isBorrowToken) {
				response = await borrowToken({
					meterNumber: confirmationData.meterNumber,
					pin,
					amount: Number.parseFloat(confirmationData.amount),
				}).unwrap();
				trackTransactionAuthorizedSuccess(selectedPaymentOption, confirmationData.amount);
				if (onBorrowSuccess) onBorrowSuccess(response.reference);
				return;
			}

			const basePayload: InitiateVendingPayload = {
				utilityId: confirmationData.utilityId,
				meterNumber: confirmationData.meterNumber,
				amount: Number.parseFloat(confirmationData.amount),
				pin,
				remoteTokenLoading: remoteTokenLoadForPayment,
			};

			if (billingDashNotificationId) {
				if (isWallet) {
					response = await initiateNotificationPayWithWallet({ billingDashNotificationId, pin, remoteTokenLoading: remoteTokenLoadForPayment }).unwrap();
				} else {
					response = await initiateNotificationPayWithCard({
						billingDashNotificationId,
						pin,
						returnUrl: `${window.location.origin}/buy-electricity`,
						paymentTypes: ["Card"],
						remoteTokenLoading: remoteTokenLoadForPayment,
					}).unwrap();
				}
			} else if (isPrepaid) {
				if (isWallet) {
					response = await initiatePrepaidPayWithWallet(basePayload).unwrap();
				} else {
					const cardPayload: InitiateVendingCardPayload = {
						...basePayload,
						returnUrl: `${window.location.origin}/buy-electricity`,
						paymentTypes,
					};
					response = await initiatePrepaidPayment(cardPayload).unwrap();
				}
			} else {
				if (isWallet) {
					response = await initiatePostpaidPayWithWallet(basePayload).unwrap();
				} else {
					const cardPayload: InitiateVendingCardPayload = {
						...basePayload,
						returnUrl: `${window.location.origin}/buy-electricity`,
						paymentTypes,
					};
					response = await initiatePostpaidPayment(cardPayload).unwrap();
				}
			}

			setPaymentData({
				paymentUrl: response.paymentUrl ?? response.returnUrl ?? (typeof response === "string" ? response : undefined),
				reference: response.reference ?? response.refference,
				vendingDetailsId: response.vendingDetailsId,
				skipRedirect: isWallet,
			});
			// The PIN travels inside the initiate call, so an accepted call is the authorization.
			trackTransactionAuthorizedSuccess(selectedPaymentOption, confirmationData.amount);
			setShowPaymentProcessor(true);
		} catch (error: any) {
			console.error("Payment initiation failed:", error);
			trackTransactionAuthorizedFailed(selectedPaymentOption, describeError(error));
			toast.error(getErrorMessage(error?.data));
		}
	};

	if (showPaymentProcessor && paymentData) {
		return (
			<PaymentProcessor
				paymentUrl={paymentData.paymentUrl}
				reference={paymentData.reference}
				vendingDetailsId={paymentData.vendingDetailsId}
				skipRedirect={paymentData.skipRedirect}
				onSuccess={reference => onPaymentSuccess?.(reference)}
				onFailed={(reference, reason) => onPaymentFailed(reference, reason)}
				onBack={() => setShowPaymentProcessor(false)}
			/>
		);
	}

	return (
		<TransactionPinModal
			submitText={t("pin.pay")}
			isSubmitting={isLoading}
			onSubmit={handlePinSubmit}
			onForgetPin={onForgetPin}
			onClose={handleBack}
			showRemoteTokenLoad={isRemoteTokenLoadEnabled && confirmationData.showPreferRemoteTokenLoadOption}
			initialRemoteTokenLoad={initialRemoteTokenLoad}
		/>
	);
};

export default BuyElectricityTransactionPin;
